import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../Services/Firebase/FirebaseIndex';
import { useAuth } from '../Components/Auth/AuthContext';
import debug from '../utils/debug';

export const useDashboardTour = () => {
  const { currentUser, userType } = useAuth();
  const [tourState, setTourState] = useState({
    hasCompletedTour: false,
    isFirstLogin: false,
    shouldShowTour: false,
    loading: true
  });

  // Check if user has completed the tour
  const checkTourCompletion = useCallback(async () => {
    if (!currentUser?.uid || !userType) {
      setTourState(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      debug.log('useDashboardTour', 'Checking tour completion for user:', currentUser.uid);

      const userDoc = await getDoc(doc(db, 'Users', currentUser.uid));
      if (!userDoc.exists()) {
        debug.warn('useDashboardTour', 'User document not found');
        setTourState(prev => ({ ...prev, loading: false }));
        return;
      }

      const userData = userDoc.data();
      const tourCompleted = userData.tourCompleted || {};
      const tourField = userType === 'Talent' ? 'talentDashboard' : 'organiserDashboard';

      const hasCompletedTour = tourCompleted[tourField] === true;

      // Check if this is a first login (no tour completion and recent account creation)
      const accountCreatedAt = userData.createdAt?.toDate();
      const now = new Date();
      const daysSinceCreation = accountCreatedAt ?
        (now - accountCreatedAt) / (1000 * 60 * 60 * 24) : 999;

      // Consider it a "first login" if account is less than 7 days old and tour not completed
      const isFirstLogin = !hasCompletedTour && daysSinceCreation <= 7;

      // Should show tour automatically only on first login, but tour can be manually triggered anytime
      const shouldShowTour = !hasCompletedTour && isFirstLogin;

      debug.log('useDashboardTour', 'Tour state:', {
        hasCompletedTour,
        isFirstLogin,
        shouldShowTour,
        daysSinceCreation
      });

      setTourState({
        hasCompletedTour,
        isFirstLogin,
        shouldShowTour,
        loading: false
      });

    } catch (error) {
      debug.error('useDashboardTour', 'Error checking tour completion:', error);
      setTourState(prev => ({ ...prev, loading: false }));
    }
  }, [currentUser?.uid, userType]);

  // Mark tour as completed
  const markTourCompleted = useCallback(async () => {
    if (!currentUser?.uid || !userType) return;

    try {
      const userRef = doc(db, 'Users', currentUser.uid);
      const tourField = userType === 'Talent' ? 'talentDashboard' : 'organiserDashboard';

      // Initialize tourCompleted object if it doesn't exist
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data() || {};
      const tourCompleted = userData.tourCompleted || {};

      await updateDoc(userRef, {
        [`tourCompleted.${tourField}`]: true,
        tourCompletedAt: new Date(),
        // Preserve other tour completion statuses
        'tourCompleted': {
          ...tourCompleted,
          [tourField]: true
        }
      });

      setTourState(prev => ({
        ...prev,
        hasCompletedTour: true,
        shouldShowTour: false
      }));

      debug.log('useDashboardTour', 'Tour completion marked successfully');
    } catch (error) {
      debug.error('useDashboardTour', 'Error marking tour completed:', error);
    }
  }, [currentUser?.uid, userType]);

  // Reset tour state (for manual tour restart)
  const resetTourState = useCallback(() => {
    setTourState(prev => ({
      ...prev,
      shouldShowTour: true
    }));

    // Force tour trigger via custom event as fallback
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('startDashboardTour', {
        detail: { userType }
      }));
    }, 100);
  }, [userType]);

  // Function to reset shouldShowTour to false (called by DashboardTour after starting)
  const clearTourTrigger = useCallback(() => {
    setTourState(prev => ({
      ...prev,
      shouldShowTour: false
    }));
  }, []);

  // Skip tour (mark as completed without showing)
  const skipTour = useCallback(async () => {
    await markTourCompleted();
  }, [markTourCompleted]);

  // Check tour completion on mount and when user/userType changes
  useEffect(() => {
    checkTourCompletion();
  }, [checkTourCompletion]);

  return {
    ...tourState,
    markTourCompleted,
    resetTourState,
    clearTourTrigger,
    skipTour,
    refetch: checkTourCompletion
  };
};